import React from "react";
import styled from "styled-components";
import { SchoolDashboard } from "../../common/api";
import {
  COLOR_BORDER_DEFAULT,
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_SECONDARY,
} from "../../common/colors";
import { SPACE_MD } from "../../common/spacing";

const ParentCrumbs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: ${SPACE_MD}px;
  font-size: 14px;
  color: ${COLOR_TEXT_SECONDARY};
  a {
    color: inherit;
    text-decoration: none;
  }
  a:hover {
    color: ${COLOR_TEXT_PRIMARY};
    text-decoration: underline;
    text-decoration-color: ${COLOR_BORDER_DEFAULT};
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(24px, 4.5vw, 28px);
  font-weight: 600;
  line-height: 1.25;
  color: ${COLOR_TEXT_PRIMARY};
`;

type Crumb = { name: string | null; link?: string | null };

const dashboardCrumbs = (dashboard: SchoolDashboard): Crumb[] => {
  const crumbs: Crumb[] = [];
  if (dashboard.is_at_school_group_level) {
    crumbs.push({ name: dashboard.school_group_name });
  }
  if (dashboard.is_district_link_visible) {
    crumbs.push({
      name: dashboard.district_name,
      link: dashboard.district_link,
    });
  }
  if (
    dashboard.is_at_school_level ||
    dashboard.is_at_teacher_level ||
    dashboard.is_at_single_user_level ||
    dashboard.is_at_lesson_level ||
    dashboard.is_at_lesson_execution_level
  ) {
    crumbs.push({
      name: dashboard.school_name,
      link: !dashboard.is_at_school_level ? dashboard.school_link : null,
    });
  }
  if (
    (dashboard.is_at_teacher_level ||
      dashboard.is_at_single_user_level ||
      dashboard.is_at_lesson_level ||
      dashboard.is_at_lesson_execution_level) &&
    dashboard.teacher_name
  ) {
    crumbs.push({ name: dashboard.teacher_name, link: dashboard.teacher_link });
  }
  if (
    dashboard.is_at_single_user_level ||
    dashboard.is_at_lesson_execution_level
  ) {
    crumbs.push({
      name: dashboard.single_user_name,
      link: !dashboard.is_at_single_user_level
        ? dashboard.single_user_link
        : null,
    });
  }
  if (dashboard.is_at_lesson_execution_level || dashboard.is_at_lesson_level) {
    crumbs.push({ name: dashboard.lesson_title });
  }
  return crumbs;
};

export const Breadcrumbs = ({
  dashboard,
  navigate,
}: {
  dashboard: SchoolDashboard;
  navigate: (string) => (event: React.MouseEvent) => void;
}) => {
  const crumbs = dashboardCrumbs(dashboard);
  const parents = crumbs.slice(0, -1);
  return (
    <nav aria-label="breadcrumb">
      {parents.length > 0 && (
        <ParentCrumbs>
          {parents.map((crumb, index) => (
            <React.Fragment key={index}>
              {index > 0 && <span>›</span>}
              <a href={crumb.link ?? undefined} onClick={navigate(crumb.link)}>
                {crumb.name}
              </a>
            </React.Fragment>
          ))}
        </ParentCrumbs>
      )}
      <Title aria-current="page">{crumbs[crumbs.length - 1]?.name}</Title>
    </nav>
  );
};
